import React, { useMemo, useState } from "react";
import { useMutation } from "urql";
import { Field, Form, Formik, getIn, useFormikContext } from "formik";
import * as Yup from "yup";

import { mapping } from "../../../../common/src";
import { ReactTable } from "../../../../components/src/react-table/ReactTable";
import { createReactTableColumnHelper, ReactTableColumnDef } from "../../../../components/src/react-table/features";
import { DefaultColumnFilter } from "../../../../components/src/react-table/ReactTableFilters";
import { Button } from "../../../../components/src/Button";

import { UPSERT_T_ACCOUNT_CHART } from "../../../../graphql/src/taccountchart2/queries";

import { TAccountTypeEnum } from "../../types.generated";
import type { UpsertTAccountChart, UpsertTAccountChartInput, UpsertTAccountInput } from "../../types.generated";

const GROUP_FIELDS = ["group1", "group2", "group3", "group4", "group5"] as const;

// form values use "" for empty, converted to null on save
type TAccountValues = Record<"number" | "type" | "description" | (typeof GROUP_FIELDS)[number], string>;

interface TAccountsFormValues {
    tAccounts: TAccountValues[];
}

// the group hierarchy in mapping decides which groups are valid below the selected parent groups
const getGroupOptions = (tAccount: TAccountValues, level: number): string[] => {
    let node = mapping as Record<string, any>;
    for (let i = 0; i < level; i++) {
        const parent = tAccount[GROUP_FIELDS[i]];
        if (!parent || !node[parent]) return [];
        node = node[parent];
    }
    return Object.keys(node).filter((key) => key !== "translation" && key !== "sorting");
};

// urql adds __typename to query results, it is not allowed in inputs
const withoutTypename = <T,>(value: T): T => JSON.parse(JSON.stringify(value, (key, v) => (key === "__typename" ? undefined : v)));

const validationSchema = Yup.object({
    tAccounts: Yup.array()
        .of(Yup.object({ number: Yup.string().trim().required("Number is required") }))
        .test("unique-numbers", "Duplicated number", function (tAccounts) {
            const numbers = (tAccounts ?? []).map((tAccount) => (tAccount.number ?? "").trim());
            const duplicateIndex = numbers.findIndex((number, index) => number && numbers.indexOf(number) !== index);
            if (duplicateIndex === -1) return true;
            return this.createError({
                path: `tAccounts.${duplicateIndex}.number`,
                message: "Duplicated number " + numbers[duplicateIndex]
            });
        })
});

const CellError = ({ name }: { name: string }): React.ReactElement => {
    const { errors, touched, submitCount } = useFormikContext<TAccountsFormValues>();
    const error = getIn(errors, name);
    if (!error || (!getIn(touched, name) && submitCount === 0)) return null;
    return <div className="form-field-error">{error}</div>;
};

const TextCell = ({ name }: { name: string }): React.ReactElement => (
    <div>
        <Field name={name} className="form-input" aria-label={name} />
        <CellError name={name} />
    </div>
);

const TypeCell = ({ name }: { name: string }): React.ReactElement => (
    <Field as="select" name={name} className="form-input" aria-label={name}>
        <option value=""></option>
        {Object.values(TAccountTypeEnum).map((type) => (
            <option key={type} value={type}>
                {type}
            </option>
        ))}
    </Field>
);

const GroupCell = ({ index, level }: { index: number; level: number }): React.ReactElement => {
    const { values, setFieldValue } = useFormikContext<TAccountsFormValues>();
    const tAccount = values.tAccounts[index];
    const name = `tAccounts.${index}.${GROUP_FIELDS[level]}`;
    const options = getGroupOptions(tAccount, level);
    const value = tAccount[GROUP_FIELDS[level]];
    return (
        <select
            name={name}
            aria-label={name}
            className="form-input"
            value={value}
            disabled={level > 0 && !tAccount[GROUP_FIELDS[level - 1]]}
            onChange={(e) => {
                setFieldValue(name, e.target.value);
                // groups below depend on this group, clear them when no longer valid
                const updated = { ...tAccount, [GROUP_FIELDS[level]]: e.target.value };
                for (let i = level + 1; i < GROUP_FIELDS.length; i++) {
                    if (!getGroupOptions(updated, i).includes(updated[GROUP_FIELDS[i]])) {
                        updated[GROUP_FIELDS[i]] = "";
                        setFieldValue(`tAccounts.${index}.${GROUP_FIELDS[i]}`, "");
                    }
                }
            }}
        >
            <option value=""></option>
            {/* keep a stored value that is not in the hierarchy visible so it is not lost silently */}
            {value && !options.includes(value) ? <option value={value}>{value} (invalid)</option> : null}
            {options.map((option) => (
                <option key={option} value={option}>
                    {option}
                </option>
            ))}
        </select>
    );
};

const RemoveCell = ({ index }: { index: number }): React.ReactElement => {
    const { values, setFieldValue } = useFormikContext<TAccountsFormValues>();
    return (
        <Button
            type="button"
            size="sm"
            variant="secondary"
            aria-label={`Remove account ${values.tAccounts[index].number}`}
            onClick={() =>
                setFieldValue(
                    "tAccounts",
                    values.tAccounts.filter((_, i) => i !== index)
                )
            }
        >
            Remove
        </Button>
    );
};

interface TAccountsFormProps {
    tAccountChart: UpsertTAccountChart;
    onDone: (saved: boolean) => void;
}

export function TAccountsForm({ tAccountChart, onDone }: TAccountsFormProps): React.ReactElement {
    const [_, upsertTAccountChart] = useMutation(UPSERT_T_ACCOUNT_CHART);
    const [saveError, setSaveError] = useState<string | null>(null);

    const initialValues: TAccountsFormValues = useMemo(
        () => ({
            tAccounts: (tAccountChart.tAccounts ?? []).map((tAccount) => ({
                number: tAccount.number ?? "",
                type: tAccount.type ?? "",
                description: tAccount.description ?? "",
                group1: tAccount.group1 ?? "",
                group2: tAccount.group2 ?? "",
                group3: tAccount.group3 ?? "",
                group4: tAccount.group4 ?? "",
                group5: tAccount.group5 ?? ""
            }))
        }),
        [tAccountChart]
    );

    // cells read and write their row through the form context, so columns do not change while typing
    const columns = useMemo<ReactTableColumnDef<TAccountValues>[]>(() => {
        const columnHelper = createReactTableColumnHelper<TAccountValues>();

        return [
            columnHelper.accessor("number", {
                header: "Number",
                cell: ({ row }) => <TextCell name={`tAccounts.${row.index}.number`} />,
                size: 110,
                filter: DefaultColumnFilter
            }),
            columnHelper.accessor("type", {
                header: "Type",
                cell: ({ row }) => <TypeCell name={`tAccounts.${row.index}.type`} />,
                size: 110
            }),
            columnHelper.accessor("description", {
                header: "Description",
                cell: ({ row }) => <TextCell name={`tAccounts.${row.index}.description`} />,
                size: 250,
                filter: DefaultColumnFilter
            }),
            ...GROUP_FIELDS.map((groupField, level) =>
                columnHelper.accessor(groupField, {
                    header: "Group " + (level + 1),
                    cell: ({ row }) => <GroupCell index={row.index} level={level} />,
                    size: 140
                })
            ),
            columnHelper.display({
                id: "remove",
                header: "",
                cell: ({ row }) => <RemoveCell index={row.index} />,
                size: 90
            })
        ];
    }, []);

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={async (values) => {
                setSaveError(null);
                const tAccounts: UpsertTAccountInput[] = values.tAccounts.map((tAccount) => ({
                    number: tAccount.number.trim(),
                    type: (tAccount.type || null) as UpsertTAccountInput["type"],
                    description: tAccount.description || null,
                    group1: (tAccount.group1 || null) as UpsertTAccountInput["group1"],
                    group2: (tAccount.group2 || null) as UpsertTAccountInput["group2"],
                    group3: (tAccount.group3 || null) as UpsertTAccountInput["group3"],
                    group4: (tAccount.group4 || null) as UpsertTAccountInput["group4"],
                    group5: (tAccount.group5 || null) as UpsertTAccountInput["group5"]
                }));
                // an update replaces the whole chart, send the mappings unchanged
                const input: UpsertTAccountChartInput = {
                    _id: tAccountChart._id,
                    clientId: tAccountChart.clientId,
                    name: tAccountChart.name,
                    version: tAccountChart.version,
                    tAccounts,
                    tAccountMappings: withoutTypename(tAccountChart.tAccountMappings ?? []),
                    updateTimestamp: tAccountChart.updateTimestamp
                };
                const result = await upsertTAccountChart({ input });
                if (result.error) {
                    setSaveError(result.error.graphQLErrors.length > 0 ? result.error.graphQLErrors[0].message : result.error.message);
                } else {
                    onDone(true);
                }
            }}
        >
            {({ values, setFieldValue, isSubmitting, dirty }) => (
                <Form autoComplete="off">
                    <div className="flex gap-2 mb-2">
                        <Button type="submit" size="sm" disabled={isSubmitting || !dirty}>
                            Save
                        </Button>
                        <Button type="button" size="sm" variant="secondary" disabled={isSubmitting} onClick={() => onDone(false)}>
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            disabled={isSubmitting}
                            onClick={() =>
                                setFieldValue("tAccounts", [
                                    ...values.tAccounts,
                                    {
                                        number: "",
                                        type: "",
                                        description: "",
                                        group1: "",
                                        group2: "",
                                        group3: "",
                                        group4: "",
                                        group5: ""
                                    }
                                ])
                            }
                        >
                            Add account
                        </Button>
                    </div>
                    {saveError ? <div className="form-field-error mb-2">{saveError}</div> : null}
                    <ReactTable columns={columns} data={values.tAccounts} initialPageSize={1000} />
                </Form>
            )}
        </Formik>
    );
}
